import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Flex,
  Heading,
  Text,
  TextField,
  View,
} from '@aws-amplify/ui-react';
import Loadingmini from '../components/loadingmini';
import { api3Get, api3Post } from '../api/api3Client';

const TABS = [
  { id: 'vehicles', label: 'Vehicles' },
  { id: 'drivers', label: 'Drivers' },
  { id: 'sync', label: 'Out of sync' },
];

const DIFF_LABELS = {
  vin_missing_on_carrier: 'VIN on Ago, missing on carrier',
  vin_extra_on_carrier: 'VIN on carrier, not on Ago',
  driver_missing_on_carrier: 'Driver approved on Ago, missing on carrier',
  driver_extra_on_carrier: 'Driver on carrier, not approved on Ago',
};

const ENROLLMENT_LABELS = {
  enrolled: 'Enrolled',
  pending_add: 'Pending (carrier processing)',
  enrolled_elsewhere: 'On another policy',
  not_enrolled: 'Not enrolled',
};

function formatDay(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    const ms = value > 1e12 ? value : value * 1000;
    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString().slice(0, 10);
  }
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatLastReservation(d) {
  if (d.lastReservationEnd == null || d.lastReservationEnd === '') return 'none';
  const day = formatDay(d.lastReservationEnd) || String(d.lastReservationEnd);
  if (d.lastReservationOpen === true) return `${day} (open)`;
  if (d.lastReservationOpen === false) return `${day} (closed)`;
  return day;
}

function td(extra = {}) {
  return { padding: '0.4rem 0.5rem', ...extra };
}

function TabBar({ active, onChange, badges }) {
  return (
    <Flex gap="0.5rem" marginTop="1rem" marginBottom="0.5rem" wrap="wrap">
      {TABS.map((t) => {
        const badge = badges?.[t.id];
        return (
          <Button
            key={t.id}
            size="small"
            variation={active === t.id ? 'primary' : undefined}
            onClick={() => onChange(t.id)}
          >
            {t.label}
            {badge != null && badge > 0 ? ` (${badge})` : ''}
          </Button>
        );
      })}
    </Flex>
  );
}

/**
 * Confirm dialog shown before EVERY action that requests a change on the
 * insurer side (enroll/unenroll VIN, remove driver, resolve actions).
 * Nothing is sent to the carrier until the user explicitly confirms here.
 */
function ConfirmActionBox({ confirm, busy, error, onConfirm, onCancel, notes, setNotes }) {
  if (!confirm) return null;
  return (
    <View
      marginTop="1rem"
      padding="1rem"
      backgroundColor="#fff8e6"
      style={{ border: '1px solid #e0b95c', borderRadius: '6px' }}
    >
      <Heading level={6}>{confirm.title}</Heading>
      <Text marginTop="0.25rem">{confirm.description}</Text>
      {confirm.carrierWarning ? (
        <Text marginTop="0.5rem" fontWeight="bold">
          This sends a change request to the insurance carrier ({confirm.carrierWarning}).
        </Text>
      ) : null}
      {confirm.allowNotes ? (
        <TextField
          label="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          marginTop="0.5rem"
        />
      ) : null}
      {error ? (
        <Alert variation="error" marginTop="0.5rem">
          {error}
        </Alert>
      ) : null}
      <Flex gap="0.5rem" marginTop="0.75rem">
        <Button
          size="small"
          variation="primary"
          isLoading={busy}
          onClick={onConfirm}
        >
          {confirm.confirmLabel || 'Confirm'}
        </Button>
        <Button size="small" onClick={onCancel} isDisabled={busy}>
          Cancel
        </Button>
      </Flex>
    </View>
  );
}

export default function Insuranceprogram() {
  const { programMasterId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState('vehicles');
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState('');
  const [notes, setNotes] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [driverFilter, setDriverFilter] = useState('');
  const [lastActionMessage, setLastActionMessage] = useState('');

  const rosterPath = `/insurance/programs/${programMasterId}/roster`;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = (params.get('tab') || '').toLowerCase();
    if (TABS.some((t) => t.id === tabParam)) setTab(tabParam);
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      setError('');
      try {
        const result = await api3Get(rosterPath);
        if (!cancelled) setData(result);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load roster');
      }
      if (!cancelled) setIsLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [rosterPath, refreshKey]);

  function updateTab(next) {
    setTab(next);
    setConfirm(null);
    setConfirmError('');
    const params = new URLSearchParams(location.search);
    params.set('tab', next);
    navigate(
      { pathname: location.pathname, search: `?${params.toString()}` },
      { replace: true }
    );
  }

  function requestAction(confirmSpec) {
    setConfirm(confirmSpec);
    setConfirmError('');
    setNotes('');
    setLastActionMessage('');
  }

  async function runConfirmedAction() {
    if (!confirm) return;
    setConfirmBusy(true);
    setConfirmError('');
    try {
      const body = { ...confirm.body };
      if (confirm.allowNotes && notes.trim()) body.notes = notes.trim();
      const result = await api3Post(rosterPath, body);
      setConfirm(null);
      setLastActionMessage(result?.message || 'Done.');
      setRefreshKey((k) => k + 1);
    } catch (e) {
      setConfirmError(e.message || 'Action failed');
    }
    setConfirmBusy(false);
  }

  const program = data?.program;
  const allowed = data?.allowedActions || {};
  const agoVehicles = data?.agoRoster?.vehicles || [];
  const agoDrivers = data?.agoRoster?.drivers || [];
  const carrierOk = Boolean(data?.carrierRoster?.ok);
  const carrierVins = useMemo(
    () => new Set((data?.carrierRoster?.vehicles || []).map((v) => v.vin)),
    [data]
  );
  const carrierDLs = useMemo(
    () =>
      new Set(
        (data?.carrierRoster?.drivers || []).map(
          (d) => `${(d.dlState || '').toUpperCase()}:${(d.dlNumber || '').toUpperCase()}`
        )
      ),
    [data]
  );
  const liveDiffs = data?.liveDiffs || [];
  const openDiscrepancies = data?.openDiscrepancies || [];
  const policyLabel = program?.policyNumber || `program ${programMasterId}`;

  const filteredVehicles = useMemo(() => {
    const f = vehicleFilter.trim().toUpperCase();
    if (!f) return agoVehicles;
    return agoVehicles.filter(
      (v) =>
        (v.vin || '').includes(f) ||
        (v.name || '').toUpperCase().includes(f) ||
        (v.plate || '').toUpperCase().includes(f) ||
        (v.make || '').toUpperCase().includes(f) ||
        (v.model || '').toUpperCase().includes(f)
    );
  }, [agoVehicles, vehicleFilter]);

  const filteredDrivers = useMemo(() => {
    const endorsed = agoDrivers.filter((d) => d.endorsementBucket !== 'not_endorsed');
    const f = driverFilter.trim().toUpperCase();
    if (!f) return endorsed;
    return endorsed.filter((d) => {
      const name = `${d.nameFirst || ''} ${d.nameLast || ''}`.toUpperCase();
      return (
        name.includes(f) ||
        (d.email || '').toUpperCase().includes(f) ||
        (d.dlNumber || '').toUpperCase().includes(f) ||
        (d.dlState || '').toUpperCase().includes(f) ||
        String(d.driverId || '').includes(f)
      );
    });
  }, [agoDrivers, driverFilter]);

  const enrolledCount = agoVehicles.filter((v) => v.enrollmentStatus === 'enrolled').length;
  const approvedDrivers = agoDrivers.filter((d) => d.endorsementBucket === 'approved');

  function confirmEnrollVehicle(v) {
    requestAction({
      title: `Enroll VIN ${v.vin}`,
      description: `Add ${v.name || v.vin} (${v.year || ''} ${v.make || ''} ${v.model || ''}) to policy ${policyLabel}. The carrier processes adds asynchronously — the VIN will show as pending until approved.`,
      carrierWarning: program?.carrierCompany || 'carrier',
      confirmLabel: 'Yes, enroll this VIN',
      body: { action: 'enroll_vehicle', vehicleId: v.vehicleId },
    });
  }

  function confirmUnenrollVehicle(v) {
    requestAction({
      title: `Remove VIN ${v.vin} from policy`,
      description: `Remove ${v.name || v.vin} from policy ${policyLabel}. The vehicle will no longer be covered by this program.`,
      carrierWarning: program?.carrierCompany || 'carrier',
      confirmLabel: 'Yes, remove this VIN',
      body: { action: 'unenroll_vehicle', vehicleId: v.vehicleId },
    });
  }

  function confirmRemoveDriver(d) {
    const name = `${d.nameFirst || ''} ${d.nameLast || ''}`.trim() || `driver ${d.driverId}`;
    requestAction({
      title: `Remove driver ${name}`,
      description: `Remove ${name} (DL ${d.dlState || ''} ${d.dlNumber || ''}) from policy ${policyLabel}. They will no longer be endorsed; the app is the only way to re-add a driver.`,
      carrierWarning: program?.carrierCompany || 'carrier',
      confirmLabel: 'Yes, remove this driver',
      body: { action: 'remove_driver', driverId: d.driverId },
    });
  }

  function confirmRevokeAgoDriver(diff) {
    requestAction({
      title: `Mark driver as not endorsed in Ago`,
      description: `The carrier no longer lists this driver (${diff.subjectKey}). This marks the Ago endorsement revoked to match the carrier. No carrier request is sent.`,
      confirmLabel: 'Yes, revoke in Ago',
      body: { action: 'revoke_ago_driver', driverId: diff.driverId },
    });
  }

  function confirmIgnoreDiff(diff) {
    requestAction({
      title: 'Ignore this discrepancy',
      description: `Mark "${DIFF_LABELS[diff.diffType] || diff.diffType}: ${diff.subjectKey}" as ignored. It will not be reported again unless it reappears after being cleared.`,
      confirmLabel: 'Ignore',
      allowNotes: true,
      body: {
        action: 'ignore_discrepancy',
        diffType: diff.diffType,
        subjectKey: diff.subjectKey,
      },
    });
  }

  function vehicleActionButtons(v) {
    if (v.enrollmentStatus === 'enrolled' && allowed.unenrollVehicle) {
      return (
        <Button size="small" onClick={() => confirmUnenrollVehicle(v)}>
          Unenroll
        </Button>
      );
    }
    if (
      (v.enrollmentStatus === 'not_enrolled' || v.enrollmentStatus === 'enrolled_elsewhere') &&
      allowed.enrollVehicle
    ) {
      return (
        <Button size="small" onClick={() => confirmEnrollVehicle(v)}>
          Enroll
        </Button>
      );
    }
    if (v.enrollmentStatus === 'pending_add') {
      return <Text fontSize="0.85rem">awaiting carrier</Text>;
    }
    return null;
  }

  function diffActionButtons(diff) {
    const buttons = [];
    if (diff.diffType === 'vin_missing_on_carrier' && allowed.enrollVehicle && diff.vehicleId) {
      buttons.push(
        <Button
          key="push"
          size="small"
          onClick={() =>
            confirmEnrollVehicle({
              vehicleId: diff.vehicleId,
              vin: diff.vin,
              name: diff.vin,
            })
          }
        >
          Re-send to carrier
        </Button>
      );
    }
    if (diff.diffType === 'vin_extra_on_carrier' && allowed.unenrollVehicle && diff.vehicleId) {
      buttons.push(
        <Button
          key="pushremove"
          size="small"
          onClick={() =>
            confirmUnenrollVehicle({
              vehicleId: diff.vehicleId,
              vin: diff.vin,
              name: diff.vin,
            })
          }
        >
          Remove from carrier
        </Button>
      );
    }
    if (diff.diffType === 'driver_missing_on_carrier' && diff.driverId) {
      buttons.push(
        <Button key="pull" size="small" onClick={() => confirmRevokeAgoDriver(diff)}>
          Revoke in Ago
        </Button>
      );
    }
    if (diff.diffType === 'driver_extra_on_carrier' && allowed.removeDriver && diff.driverId) {
      buttons.push(
        <Button
          key="removedrv"
          size="small"
          onClick={() =>
            confirmRemoveDriver({
              driverId: diff.driverId,
              nameFirst: '',
              nameLast: '',
              dlNumber: diff.dlNumber,
              dlState: diff.dlState,
            })
          }
        >
          Remove from carrier
        </Button>
      );
    }
    buttons.push(
      <Button key="ignore" size="small" onClick={() => confirmIgnoreDiff(diff)}>
        Ignore
      </Button>
    );
    return <Flex gap="0.4rem">{buttons}</Flex>;
  }

  return (
    <View padding="1rem">
      <Flex justifyContent="space-between" alignItems="center" wrap="wrap">
        <Heading level={3}>
          {program?.name || 'Policy'} — {program?.policyNumber || ''}
        </Heading>
        <Link to="/insurance-programs">All policies (Ago list)</Link>
      </Flex>
      <Text marginTop="0.25rem" fontSize="0.9rem">
        Live carrier compare. Enroll, unenroll, and remove are on this page.
      </Text>
      {program ? (
        <Text marginTop="0.25rem">
          Fleet {program.fleetId} · Carrier {program.carrierCompany || '—'} · VIN
          authority: {program.vehicleEndorsementAuthority} · Driver authority:{' '}
          {program.driverEndorsementAuthority}
          {program.inactivityOffboard?.enabled
            ? ` · Inactivity auto-offboard ON (${program.inactivityOffboard.noResDays}d idle / ${program.inactivityOffboard.minTenureDays}d tenure)`
            : ''}
        </Text>
      ) : null}
      {!carrierOk && data ? (
        <Alert variation="warning" marginTop="0.5rem">
          Live carrier roster unavailable
          {data?.carrierRoster?.error ? `: ${data.carrierRoster.error}` : ''} — showing
          Ago data only; sync status cannot be computed.
        </Alert>
      ) : null}
      {error ? (
        <Alert variation="error" marginTop="0.5rem" isDismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {lastActionMessage ? (
        <Alert
          variation="success"
          marginTop="0.5rem"
          isDismissible
          onDismiss={() => setLastActionMessage('')}
        >
          {lastActionMessage}
        </Alert>
      ) : null}

      <TabBar
        active={tab}
        onChange={updateTab}
        badges={{
          vehicles: enrolledCount,
          drivers: approvedDrivers.length,
          sync: liveDiffs.length,
        }}
      />

      <ConfirmActionBox
        confirm={confirm}
        busy={confirmBusy}
        error={confirmError}
        onConfirm={runConfirmedAction}
        onCancel={() => setConfirm(null)}
        notes={notes}
        setNotes={setNotes}
      />

      {isLoading ? (
        <Loadingmini />
      ) : !data ? null : tab === 'vehicles' ? (
        <View marginTop="0.5rem">
          <TextField
            label="Filter by VIN / name / plate"
            labelHidden
            placeholder="Filter by VIN / name / plate"
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
            width="20rem"
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                <th style={td()}>Vehicle</th>
                <th style={td()}>Status</th>
                <th style={td()}>VIN</th>
                <th style={td()}>Plate</th>
                <th style={td()}>Ago status</th>
                <th style={td()}>On carrier</th>
                <th style={td()}>Legacy sync</th>
                <th style={td()}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((v) => (
                <tr key={v.vehicleId} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={td()}>
                    {v.name || '—'}
                    <br />
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>
                      {[v.year, v.make, v.model].filter(Boolean).join(' ')}
                    </span>
                  </td>
                  <td style={td()}>{v.vehicleStatus || '—'}</td>
                  <td style={td()}>{v.vin}</td>
                  <td style={td()}>{v.plate || '—'}</td>
                  <td style={td()}>{ENROLLMENT_LABELS[v.enrollmentStatus] || v.enrollmentStatus}</td>
                  <td style={td()}>
                    {!carrierOk ? '—' : carrierVins.has(v.vin) ? (
                      <Badge variation="success">yes</Badge>
                    ) : (
                      <Badge>no</Badge>
                    )}
                  </td>
                  <td style={td()}>
                    {v.legacyInSync ? '' : <Badge variation="warning">legacy drift</Badge>}
                  </td>
                  <td style={td()}>{vehicleActionButtons(v)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Text fontSize="0.85rem" color="gray" marginTop="0.5rem">
            To add a car that is not listed here, create it on Assets first —
            this page only manages insurance enrollment for existing fleet
            vehicles.
          </Text>
        </View>
      ) : tab === 'drivers' ? (
        <View marginTop="0.5rem">
          <TextField
            label="Filter by name / email / DL"
            labelHidden
            placeholder="Filter by name, email, or DL"
            value={driverFilter}
            onChange={(e) => setDriverFilter(e.target.value)}
            width="20rem"
          />
          <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                <th style={td()}>Driver</th>
                <th style={td()}>DL</th>
                <th style={td()}>Enrolled</th>
                <th style={td()}>Last reservation end</th>
                <th style={td()}>Ago status</th>
                <th style={td()}>On carrier</th>
                <th style={td()}>Legacy sync</th>
                <th style={td()}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrivers.map((d) => {
                const key = `${(d.dlState || '').toUpperCase()}:${(d.dlNumber || '').toUpperCase().replace(/[-\s#`'&]/g, '')}`;
                return (
                  <tr key={d.driverId} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={td()}>
                      {`${d.nameFirst || ''} ${d.nameLast || ''}`.trim() || d.driverId}
                      <br />
                      <span style={{ fontSize: '0.8rem', color: '#666' }}>{d.email || ''}</span>
                    </td>
                    <td style={td()}>
                      {d.dlState} {d.dlNumber}
                    </td>
                    <td style={td()}>{formatDay(d.enrolledAt) || '—'}</td>
                    <td style={td()}>{formatLastReservation(d)}</td>
                    <td style={td()}>
                      {d.endorsementBucket === 'approved' ? (
                        <Badge variation="success">approved</Badge>
                      ) : (
                        <Badge variation="info">{d.status}</Badge>
                      )}
                    </td>
                    <td style={td()}>
                      {!carrierOk ? '—' : carrierDLs.has(key) ? (
                        <Badge variation="success">yes</Badge>
                      ) : (
                        <Badge>no</Badge>
                      )}
                    </td>
                    <td style={td()}>
                      {d.legacyInSync ? '' : <Badge variation="warning">legacy drift</Badge>}
                    </td>
                    <td style={td()}>
                      {allowed.removeDriver ? (
                        <Button size="small" onClick={() => confirmRemoveDriver(d)}>
                          Remove
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Text fontSize="0.85rem" color="gray" marginTop="0.5rem">
            Drivers are added through the app onboarding flow only — there is no
            portal add.
          </Text>
        </View>
      ) : (
        <View marginTop="0.5rem">
          {!carrierOk ? (
            <Text>Carrier roster unavailable — live sync status cannot be computed.</Text>
          ) : liveDiffs.length === 0 ? (
            <Alert variation="success">
              Ago and {program?.carrierCompany || 'carrier'} rosters are in sync.
            </Alert>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '2px solid #ccc' }}>
                  <th style={td()}>Type</th>
                  <th style={td()}>Subject</th>
                  <th style={td()}>Detail</th>
                  <th style={td()}>Resolve</th>
                </tr>
              </thead>
              <tbody>
                {liveDiffs.map((diff) => (
                  <tr
                    key={`${diff.diffType}:${diff.subjectKey}`}
                    style={{ borderBottom: '1px solid #eee' }}
                  >
                    <td style={td()}>{DIFF_LABELS[diff.diffType] || diff.diffType}</td>
                    <td style={td()}>{diff.vin || diff.subjectKey}</td>
                    <td style={td()}>{diff.detail}</td>
                    <td style={td()}>{diffActionButtons(diff)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {openDiscrepancies.length > 0 ? (
            <View marginTop="1rem">
              <Heading level={6}>Open since (overnight history)</Heading>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #ccc' }}>
                    <th style={td()}>Type</th>
                    <th style={td()}>Subject</th>
                    <th style={td()}>First seen</th>
                    <th style={td()}>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {openDiscrepancies.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={td()}>{DIFF_LABELS[row.diffType] || row.diffType}</td>
                      <td style={td()}>{row.vin || row.subjectKey}</td>
                      <td style={td()}>{row.firstSeenAt}</td>
                      <td style={td()}>{row.lastSeenAt}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </View>
          ) : null}
        </View>
      )}
    </View>
  );
}
